import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, formatApiError } from "../lib/api";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const ACTION_OPTIONS = [
    { id: "adventure_posted", label: "Post adventures" },
    { id: "gps_comment", label: "Leave trail notes" },
    { id: "save_received", label: "Get saves on your posts" },
    { id: "review_written", label: "Write reviews" },
];

export default function CreateFriendChallengeModal({ open, onOpenChange, onCreated }) {
    const navigate = useNavigate();
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [actionKind, setActionKind] = useState("adventure_posted");
    const [target, setTarget] = useState(3);
    const [days, setDays] = useState(14);
    const [followers, setFollowers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        api.get("/friend-challenges/discover/followers")
            .then(({ data }) => setFollowers(data.items || []))
            .catch(() => {});
    }, [open]);

    const toggle = (id) => setInvites((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

    const submit = async (e) => {
        e.preventDefault();
        if (!title.trim()) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/friend-challenges", {
                title: title.trim(),
                description: description.trim(),
                action_kind: actionKind,
                target_count: Number(target) || 1,
                duration_days: Number(days) || 14,
                invite_user_ids: invites,
            });
            toast.success("Challenge created");
            onOpenChange?.(false);
            onCreated?.();
            navigate(`/friend-challenges/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="create-fc-modal" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display font-black text-xl text-midnight-pine">New friend challenge</DialogTitle>
                    <DialogDescription className="text-xs text-slate-gray">Set a goal, pick a duration, and invite mutuals.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <Field label="Title">
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            data-testid="fc-title-input"
                            placeholder="3 hikes this month"
                            className="w-full px-3 py-2 rounded-md border border-ridge-line bg-snowcap text-sm focus:border-ember-orange outline-none"
                            maxLength={80}
                            required
                        />
                    </Field>
                    <Field label="Description (optional)">
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            data-testid="fc-desc-input"
                            rows={2}
                            className="w-full px-3 py-2 rounded-md border border-ridge-line bg-snowcap text-sm focus:border-ember-orange outline-none"
                            maxLength={400}
                        />
                    </Field>
                    <Field label="Action">
                        <select
                            value={actionKind}
                            onChange={(e) => setActionKind(e.target.value)}
                            data-testid="fc-action-select"
                            className="w-full px-3 py-2 rounded-md border border-ridge-line bg-snowcap text-sm focus:border-ember-orange outline-none"
                        >
                            {ACTION_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                        </select>
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Target count">
                            <input
                                type="number" min={1} max={100}
                                value={target}
                                onChange={(e) => setTarget(e.target.value)}
                                data-testid="fc-target-input"
                                className="w-full px-3 py-2 rounded-md border border-ridge-line bg-snowcap text-sm focus:border-ember-orange outline-none"
                            />
                        </Field>
                        <Field label="Duration (days)">
                            <input
                                type="number" min={1} max={90}
                                value={days}
                                onChange={(e) => setDays(e.target.value)}
                                data-testid="fc-days-input"
                                className="w-full px-3 py-2 rounded-md border border-ridge-line bg-snowcap text-sm focus:border-ember-orange outline-none"
                            />
                        </Field>
                    </div>

                    <Field label="Invite (optional — your followers + people you follow)">
                        <div className="max-h-44 overflow-y-auto border border-ridge-line rounded-md p-2 bg-alpine-mist">
                            {followers.length === 0 ? (
                                <p className="text-xs text-slate-gray italic px-2 py-3">No mutuals yet — your challenge will be solo until friends join.</p>
                            ) : followers.map((f) => (
                                <label key={f.user_id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-trail-tan/40 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={invites.includes(f.user_id)}
                                        onChange={() => toggle(f.user_id)}
                                        data-testid={`fc-invite-${f.user_id}`}
                                    />
                                    <span className="text-sm font-semibold text-midnight-pine">{f.name}</span>
                                    {f.username && <span className="text-xs text-slate-gray">@{f.username}</span>}
                                </label>
                            ))}
                        </div>
                    </Field>

                    <div className="flex items-center justify-end gap-2 pt-1">
                        <button type="button" onClick={() => onOpenChange?.(false)} data-testid="fc-cancel-btn" className="px-3 py-2 text-sm font-bold text-slate-gray hover:text-granite">Cancel</button>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="fc-submit-btn"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-ember-orange text-white font-bold text-sm hover:bg-ember-dark active:scale-95 transition-all disabled:opacity-50"
                        >
                            <Sparkles className="w-4 h-4" /> {submitting ? "Creating…" : "Create"}
                        </button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-[10px] font-bold uppercase tracking-widest text-slate-gray mb-1">{label}</span>
            {children}
        </label>
    );
}
